import { useMemo } from 'react';
import { crmStage, useOutreachRows } from '@discovr/features';
import type { OutreachRow } from '@discovr/types';
import { DataTable, type ColumnDef } from '../../../components/common/DataTable';
import { EnvelopeSimple } from '../../../design-system/icons';
import { channelColumn, creatorColumn, creatorName, crmRowLabel, lastActivityColumn } from './crmCreatorColumns';
import { CRM_ICON_BUTTON } from './CrmCard';
import { CrmListEmpty } from './CrmListEmpty';
import { CrmListFrame } from './CrmListFrame';
import { useCrmListPaging, type CrmCreatorsListProps } from './useCrmListPaging';
import { CrmStagePill } from './CrmStagePill';
import { hasThread } from './handoffStates';
import { useOpenThreadRow } from './useOpenThreadRow';

const UNKNOWN = '—';

/** The row's own Open thread button; the click stays off the row so it opens once. */
function OpenThreadButton({ row, onOpen }: { row: OutreachRow; onOpen: (row: OutreachRow) => void }) {
  if (!hasThread(row)) return null;
  return (
    <button
      type="button"
      aria-label={`Open thread with ${creatorName(row)}`}
      className={CRM_ICON_BUTTON}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(row);
      }}
    >
      <EnvelopeSimple size={18} />
    </button>
  );
}

function conversationsColumns(onOpen: (row: OutreachRow) => void): ColumnDef<OutreachRow>[] {
  return [
    creatorColumn<OutreachRow>(),
    channelColumn,
    {
      id: 'messages',
      header: 'Messages',
      minWidth: 90,
      cell: (row) => <span className="tabular-nums text-body-sm-regular text-foreground">{row.messageCount ?? UNKNOWN}</span>,
    },
    {
      id: 'stage',
      header: 'Stage',
      minWidth: 140,
      skeletonShape: 'pill',
      cell: (row) => <CrmStagePill stage={crmStage(row)} />,
    },
    lastActivityColumn,
    {
      id: 'action',
      header: 'Action',
      minWidth: 72,
      alwaysVisible: true,
      cell: (row) => <OpenThreadButton row={row} onOpen={onOpen} />,
    },
  ];
}

/** Every creator with at least one message, newest activity first. */
export function CrmConversationsTable({ campaignId, query, onClearSearch }: CrmCreatorsListProps) {
  const paging = useCrmListPaging();
  const { data, isLoading, error } = useOutreachRows(campaignId, {
    tab: 'conversations',
    q: query || undefined,
    sort: 'last_activity',
    page: paging.page,
    limit: paging.pageSize,
  });
  const openIfThread = useOpenThreadRow();
  const columns = useMemo(() => conversationsColumns(openIfThread), [openIfThread]);

  return (
    <CrmListFrame error={error} total={data?.total ?? 0} paging={paging}>
      <DataTable<OutreachRow>
        columns={columns}
        data={data?.rows ?? []}
        getRowId={(row) => row.entryId}
        loading={isLoading}
        onRowClick={openIfThread}
        rowAriaLabel={crmRowLabel}
        emptyState={<CrmListEmpty view="conversations" query={query} onClearSearch={onClearSearch} />}
      />
    </CrmListFrame>
  );
}
